<script setup lang="ts">
export interface IMenuBase {
  title: String | string | undefined;
  path?: String | string | undefined;
  show?: boolean | undefined;
  click?: () => {};
};

export interface IMenuItem extends IMenuBase {
  disabled?: boolean | undefined;
  items?: Array<IMenuBase> | undefined;
};

const { $auth } = useNuxtApp();
const auth = $auth;
const menuItems = computed<Array<IMenuItem>>((): Array<IMenuItem> => [
  {
    title: 'Údržba',
    path: '/zbl/element',
    show: auth?.value?.hasPermission('zbl-element-*'),
    disabled: (! auth?.value?.hasPermission('zbl-element-*')),
    items: [
      {
        title: 'Poruchy',
        path: '/zbl/element/defect/active',
        show: auth?.value?.hasPermission('zbl-element-defect-*')
      }
    ]
  },
  {
    title: 'OM',
    path: '/zdis',
    show: auth?.value?.hasPermission('zdis-*'),
    disabled: false,
    items: [
      {
        title: 'História',
        path: '/zdis/history',
        show: auth?.value?.hasPermission('zdis-*')
      },
      {
        title: 'Výpočty',
        path: '/zdis/calculation',
        show: auth?.value?.hasPermission('zdis-*')
      },
      {
        title: 'Nastavenie',
        path: '/zdis/config',
        show: auth?.value?.hasPermission('zdis-update')
      }
    ]
  },
  {
    title: 'Nadrozmerná Doprava',
    path: '/otp/overview',
    show: auth?.value?.hasPermission('otp-*'),
    disabled: (! auth?.value?.hasPermission('otp-overview-read')),
    items: [
      {
        title: 'Žiadosti',
        path: '/otp/request',
        show: auth?.value?.hasPermission('otp-request-*')
      },
      {
        title: 'Schválené',
        path: '/otp',
        show: auth?.value?.hasPermission('otp-read', 'otp-create', 'otp-update', 'otp-delete')
      },
      {
        title: 'Podpísané',
        path: '/otp/signature',
        show: auth?.value?.hasPermission('otp-signature-*')
      },
      {
        title: 'Ostatné',
        path: '/otp/other',
        show: auth?.value?.hasPermission('otp-attachment-*', 'otp-carrier-*', 'otp-comment-*')
      }
    ]
  },
  {
    title: 'Používatelia',
    path: '/user',
    show: auth?.value?.hasPermission('user-*', 'role-*', 'permission-*', 'notification-subscription-*', 'organization-*'),
    disabled: (! auth?.value?.hasPermission('user-*')),
    items: [
      {
        title: 'Role',
        path: '/role',
        show: auth?.value?.hasPermission('role-*')
      },
      {
        title: 'Oprávnenia',
        path: '/permission',
        show: auth?.value?.hasPermission('permission-*')
      },
      {
        title: 'Oznámenia',
        path: '/notification/subscription',
        show: auth?.value?.hasPermission('notification-subscription-*')
      },
      {
        title: 'Organizácie',
        path: '/organization',
        show: auth?.value?.hasPermission('organization-*'),
      }
    ]
  },
  {
    title: 'Register',
    path: '/register',
    show: (auth?.value?.hasPermission(
      'job-*',
      'zbl-road-*', 'zbl-section-*', 'zbl-crossroad-*', 'zbl-element-*',
      'ssc-section-*'
    ) || auth?.value?.hasRole('su', 'admin')),
    disable: (! (auth?.value?.hasPermission(
      'job-*',
      'zbl-road-*', 'zbl-section-*', 'zbl-crossroad-*', 'zbl-element-*',
      'ssc-section-*'
    ) || auth?.value?.hasRole('su', 'admin'))),
    items: [
      {
        title: 'ZBL',
        path: '/zbl',
        show: auth?.value?.hasPermission(
          'zbl-road-*', 'zbl-section-*', 'zbl-crossroad-*', 'zbl-element-*',
        )
      },
      {
        title: 'SSC',
        path: '/ssc',
        show: auth?.value?.hasPermission('ssc-section-*')
      }
    ]
  },
  {
    title: 'Kontakt',
    path: '/contact',
    show: true
  },
  {
    title: 'Prihlásenie',
    path: '/login',
    show: (! auth?.value?.logged())
  },
  {
    title: ((auth?.value?.name && auth?.value?.surname) ? (auth?.value?.name + ' ' + auth?.value?.surname) : auth?.value?.email),
    path: '/profile',
    show: auth?.value?.logged(),
    items: [
      {
        title: 'Organizácia',
        path: '/organization/edit',
        show: (auth?.value?.logged() ? auth?.value?.organization : false)
      },
      {
        title: 'Dopravcovia',
        path: '/profile/carrier',
        show: (auth?.value?.logged() ? auth?.value?.organization : false)
      },
      {
        title: 'Odhlásenie',
        path: '/',
        click: () => auth?.value?.logout(),
        show: auth?.value?.logged()
      }
    ]
  }
]);
const open = ref<boolean>(false);
const openList = ref<Object>({});
</script>

<template>
  <header>
    <nav class="navbar">
      <div id="logo">
        <NuxtLink href="/" class="scale-100 hover:scale-[1.05] ease-in duration-200">
          <span class="hidden 2xl:block w-[310px] h-16 bg-no-repeat bg-center bg-contain bg-[url(~/assets/img/logo.svg)] p-0 m-0 rounded-auto shadow-auto" />
          <span class="block 2xl:hidden w-16 h-16 bg-no-repeat bg-center bg-contain bg-[url(~/assets/img/mark.svg)] p-0 m-0 rounded-auto shadow-auto" />
        </NuxtLink>
        <div class="block 2xl:hidden">
          <button
            class="text-slate-800"
            @click="(e: Event) => (open = (! open))">
            <svg
              class="fill-primary w-4 h-4 scale-100 hover:scale-125 ease-in duration-200"
              xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
              viewBox="0 0 20 20"
              aria-hidden="true" role="img">
              <title>
                Menu
              </title>
              <path
                v-show="open"
                d="M18.278 16.864a1 1 0 01-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 01-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 011.414-1.414l4.829 4.828 4.828-4.828a1 1 0 111.414 1.414l-4.828 4.829 4.828 4.828z" />
              <path
                v-show="(! open)"
                d="M4 5h16a1 1 0 010 2H4a1 1 0 110-2zm0 6h16a1 1 0 010 2H4a1 1 0 010-2zm0 6h16a1 1 0 010 2H4a1 1 0 010-2z" />
            </svg>
          </button>
        </div>
      </div>
      <ol
        id="menu"
        :class="[
          (open && 'opened'),
          ('list-image-none')
        ]">
        <div v-for="item of menuItems.filter((item: IMenuItem) => item.show)" :key="item.path">
          <li>
            <BaseLink
              :href="item.path"
              @click="item.click"
              :size="(['xl', '2xl'].includes(useNuxtApp()?.$styleSize?.value) ? useNuxtApp()?.$styleSize?.value : 'lg')"
              :disabled="item.disabled"
              :class="[
                'font-medium hover:text-slate-900',
                ((! item.disabled) && 'scale-100 hover:scale-[1.05] ease-in duration-200')
              ]">
              {{ item.title }}
            </BaseLink>
            <button
              v-if="(item.items?.filter((subItem: IMenuBase) => subItem.show)?.length > 0)"
              class="text-primary flex items-center"
              @click="(e: Event) => (openList[item.title] = (! openList[item.title]))">
              <svg
                class="fill-primary w-4 h-4 scale-100 hover:scale-125 ease-in duration-200"
                xmlns="http://www.w3.org/2000/svg"  xmlns:xlink="http://www.w3.org/1999/xlink"
                viewBox="0 0 20 20"
                aria-hidden="true" role="img">
                <title>
                  {{ item.title }}
                </title>
                <path
                  v-show="openList[item.title]"
                  d="M18.278 16.864a1 1 0 01-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 01-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 011.414-1.414l4.829 4.828 4.828-4.828a1 1 0 111.414 1.414l-4.828 4.829 4.828 4.828z" />
                <path
                  v-show="(! openList[item.title])"
                  d="M4 5h16a1 1 0 010 2H4a1 1 0 110-2zm0 6h16a1 1 0 010 2H4a1 1 0 010-2zm0 6h16a1 1 0 010 2H4a1 1 0 010-2z" />
              </svg>
            </button>
          </li>
          <BaseCollapse
            v-if="(item.items?.filter((subItem: IMenuBase) => subItem.show)?.length > 0)"
            v-model="openList[item.title]"
            class="open:animate-[down-fade-in_400ms_ease-in-out]">
            <li v-for="subItem of item.items?.filter((subItem: IMenuBase) => subItem.show)" :key="subItem.path">
              <BaseLink
                :href="subItem.path"
                @click="subItem.click"
                :size="(['xl', '2xl'].includes(useNuxtApp()?.$styleSize?.value) ? useNuxtApp()?.$styleSize?.value : 'lg')"
                :class="[
                  'font-normal hover:text-slate-900',
                  ((! subItem.disabled) && 'scale-100 hover:scale-[1.05] ease-in duration-200')
                ]"
                :disabled="subItem.disabled">
                {{ subItem.title }}
              </BaseLink>
            </li>
          </BaseCollapse>
        </div>
      </ol>
    </nav>
  </header>
</template>

<style lang="scss" scoped>
.navbar {
  @apply my-1 flex flex-col;
  @apply 2xl:flex-row 2xl:justify-between 2xl:items-center;

  >#logo {
    @apply flex flex-row justify-between items-center;
  }

  >#menu {
    @apply flex flex-col overflow-hidden;
    @apply 2xl:flex-row 2xl:overflow-visible;

    @apply max-h-0 transition-[max-height] duration-1000;
    @apply 2xl:max-h-screen 2xl:transition-none 2xl:duration-0;

    &.opened {
      @apply max-h-screen transition-[max-height] duration-[2000ms] ease-in-out;

      /* Desktop */
      /* max-height: 100vh;
          transition: max-height 2s ease-in-out; */
    }

    >div {
      @apply border-b pl-1 2xl:border-0 2xl:px-1;

      &:not(&:last-child) {
        >:first-child {
          @apply 2xl:border-r-2 2xl:pr-1;
        }
      }

      &:last-child {
        @apply border-0;

        li {
          &:last-child {
            @apply mb-0;
          }
        }
      }

      li {
        @apply flex justify-between gap-0.5 2xl:gap-1 my-1 2xl:my-0;

        a,
        button {
          @apply p-0 2xl:pr-1;
        }
      }

      &:last-child>li {
        @apply mb-0;
      }
    }
  }
}
</style>
